import React, { useEffect, useState, useRef, useCallback } from "react";
import "./ClassiqueHero.css";
import QRCodeStyling from "qr-code-styling";
import Preloader from "../components/Preloader";
import { useParams } from "react-router-dom";
import api from "../services/api";
import { socialLinks, getSocialIcon } from "../config/socialLinks";

interface Props {
  photoBackground: string;
  inviteNom: string;
  jourSemaine: string;
  date: string;
  heure: string;
  dateISO: string;
  heureISO: string;
  invitationImage?: string;
  tableName: string;
  boissonsSansAlcool?: string[];
  boissonsAvecAlcool?: string[];
  numeroMarie: string;
  numeroPicApic: string;
  mapEmbedUrl: string;
  bride: string;
  groom: string;
  theme?: {
    background?: string;
    heart?: string;
    text?: string;
    accent?: string;
    overlay?: string;
    box?: string;
    accentLine?: string;
    inviteBoxBackground?: string;
  };
  couple?: string;
  inviteToken?: string;
  mariageId?: string;
  livreOrActive?: boolean;
}

interface InviteInfoResponse {
  success: boolean;
  mariageId: string;
  invite?: {
    nom?: string;
    table?: string;
  };
}

interface InvitationInfoResponse {
  success: boolean;
  mariageId: string;
}

interface UpdateResponse {
  success: boolean;
  message?: string;
}

const getUploadedImagePath = (imageName: string): string => {
  if (!imageName || imageName.trim() === '') return '';
  const cleanName = imageName.trim().replace(/\\/g, '/');
  
  if (cleanName.startsWith('http://') || cleanName.startsWith('https://') || cleanName.startsWith('data:')) {
    return cleanName;
  }
  
  const baseUrl = window.location.origin;
  let fileName = cleanName;
  
  if (fileName.startsWith('uploads/')) {
    fileName = fileName.replace('uploads/', '');
  }
  if (fileName.startsWith('/uploads/')) {
    fileName = fileName.replace('/uploads/', '');
  }
  if (fileName.includes('/')) {
    const parts = fileName.split('/');
    fileName = parts[parts.length - 1];
  }
  
  return `${baseUrl}/uploads/${fileName}`;
};

const parseBoissons = (boissons: string[] | undefined): string[] => {
  if (!boissons || !Array.isArray(boissons)) return [];
  return boissons
    .filter(item => item && typeof item === 'string')
    .map(item => item.trim())
    .filter(item => item.length > 0);
};

const extractGoogleMapsSrc = (iframeCode: string): { viewUrl: string } => {
  const srcMatch = iframeCode.match(/src="([^"]+)"/);
  let embedUrl = iframeCode;
  let viewUrl = "https://www.google.com/maps";
  if (srcMatch && srcMatch[1]) {
    embedUrl = srcMatch[1];
    const latMatch = embedUrl.match(/!3d(-?\d+\.\d+)/);
    const lngMatch = embedUrl.match(/!2d(-?\d+\.\d+)/);
    if (latMatch && lngMatch) {
      const lat = latMatch[1];
      const lng = lngMatch[1];
      viewUrl = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
    }
  }
  return { viewUrl };
};

const ClassiqueHero: React.FC<Props> = ({
  photoBackground,
  inviteNom,
  jourSemaine,
  date,
  heure,
  dateISO,
  heureISO,
  invitationImage,
  tableName,
  boissonsSansAlcool = [],
  boissonsAvecAlcool = [],
  numeroMarie,
  numeroPicApic,
  mapEmbedUrl,
  bride,  
  groom,
  theme = {},
  couple = "non",
  inviteToken: propInviteToken = "",
  mariageId: propMariageId = "",
  livreOrActive = true,
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [confirmed, setConfirmed] = useState(false);
  const [selectedBoisson, setSelectedBoisson] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);
  const [currentMariageId, setCurrentMariageId] = useState<string>(propMariageId || "");
  const [boissonNotification, setBoissonNotification] = useState<string | null>(null);
  const [countdown, setCountdown] = useState("");
  const [rsvpDeadline, setRsvpDeadline] = useState("");
  const [scrollY, setScrollY] = useState(0);
  
  const params = useParams();
  const urlToken = params.token as string | undefined;
  const inviteToken = urlToken || propInviteToken;
  const hasFetchedMariageId = useRef(false);
  
  const qrRef = useRef<HTMLDivElement>(null);
  const qrCode = useRef<QRCodeStyling | null>(null);
  const [qrInitialized, setQrInitialized] = useState(false);
  const heroRef = useRef<HTMLDivElement>(null);
  const backRef = useRef<HTMLElement>(null);

  const colors = {
    background: theme.background || "#faf0e6",
    heartColor: theme.heart || "#d4af37",
    textPrimary: theme.text || "#8b6b4d",
    textSecondary: theme.text || "#a88e7a",
    photoOverlay: theme.overlay || "rgba(250, 240, 230, 0.3)",
    boxBackground: theme.box || "rgba(255, 255, 255, 0.8)",
    footerBackground: theme.accent || "#c48f7a",
    buttonPrimary: theme.heart || "#d4af37",
    boxBorder: theme.accentLine || "#e8d5c0",
    accentLine: theme.accentLine || "#e8d5c0",
    inviteBoxBackground: theme.inviteBoxBackground || "rgba(255, 255, 255, 0.9)",
  };

  const cleanedBoissonsSansAlcool = parseBoissons(boissonsSansAlcool);
  const cleanedBoissonsAvecAlcool = parseBoissons(boissonsAvecAlcool);

  const { viewUrl: googleMapsViewUrl } = extractGoogleMapsSrc(mapEmbedUrl);

  useEffect(() => {
    const oldMeta = document.querySelectorAll('meta[property^="og:"]');
    oldMeta.forEach(meta => meta.remove());
    
    const meta = document.createElement('meta');
    meta.setAttribute('property', 'og:title');
    meta.setAttribute('content', `Mariage de ${bride} & ${groom}`);
    document.head.appendChild(meta);
    
    const meta2 = document.createElement('meta');
    meta2.setAttribute('property', 'og:description');
    meta2.setAttribute('content', 'Nous avons l\'honneur de vous inviter à célébrer notre mariage');
    document.head.appendChild(meta2);
    
    const meta3 = document.createElement('meta');
    meta3.setAttribute('property', 'og:image');
    meta3.setAttribute('content', getUploadedImagePath(invitationImage || photoBackground));
    document.head.appendChild(meta3);
    
    const meta4 = document.createElement('meta');
    meta4.setAttribute('property', 'og:url');
    meta4.setAttribute('content', window.location.href);
    document.head.appendChild(meta4);
    
    const meta5 = document.createElement('meta');
    meta5.setAttribute('property', 'og:type');
    meta5.setAttribute('content', 'website');
    document.head.appendChild(meta5);
    
    return () => {
      const metas = document.querySelectorAll('meta[property^="og:"]');
      metas.forEach(meta => meta.remove());
    };
  }, [bride, groom, invitationImage, photoBackground]);

  useEffect(() => {
    if (hasFetchedMariageId.current) return;
    
    const fetchMariageId = async () => {
      hasFetchedMariageId.current = true;
      if (propMariageId && propMariageId.trim() && propMariageId !== 'unknown') {
        setCurrentMariageId(propMariageId);
        return;
      }
      if (!inviteToken) {
        setCurrentMariageId('unknown');
        return;
      }
      try {
        const savedData = localStorage.getItem(`invite_${inviteToken}`);
        if (savedData) {
          const parsedData = JSON.parse(savedData);
          if (parsedData.mariageId && parsedData.mariageId !== 'unknown') {
            setCurrentMariageId(parsedData.mariageId);
            return;
          }
        }
      } catch (error) {}
      try {
        const response = await api.get<InviteInfoResponse>(`/invites/info/${inviteToken}`);
        if (response.data.success && response.data.mariageId) {
          setCurrentMariageId(response.data.mariageId);
          localStorage.setItem(`invite_${inviteToken}`, JSON.stringify({
            mariageId: response.data.mariageId,
            token: inviteToken,
            nom: response.data.invite?.nom || inviteNom,
            table: response.data.invite?.table || tableName,
            timestamp: new Date().toISOString()
          }));
          return;
        }
        const fallbackResponse = await api.get<InvitationInfoResponse>(`/invitation-info/${inviteToken}`);
        if (fallbackResponse.data.success && fallbackResponse.data.mariageId) {
          setCurrentMariageId(fallbackResponse.data.mariageId);
          return; 
        }
        setCurrentMariageId('unknown');
      } catch (error) {
        setCurrentMariageId('unknown');
      }
    };
    fetchMariageId();
  }, [propMariageId, inviteToken, inviteNom, tableName]);

  useEffect(() => {
    let ticking = false;

    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          setScrollY(window.scrollY);
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const saveChoicesToBackend = useCallback(async (statut: string, boissonPreferee?: string) => {
    if (!inviteToken) {
      setSaveMessage("❌ Token d'invitation manquant.");
      return;
    }

    const localData = {
      statut,
      boissonPreferee: boissonPreferee || null,
      mariageId: currentMariageId || 'unknown',
      inviteToken,
      timestamp: new Date().toISOString(),
      inviteNom,
      bride,
      groom
    };
    
    localStorage.setItem(`invite_${inviteToken}`, JSON.stringify(localData));
    
    if (!currentMariageId || currentMariageId === 'unknown') {
      setSaveMessage("⚠️ En attente de l'ID du mariage... Sauvegarde locale.");
      return;
    }

    setIsSaving(true);
    setSaveMessage(null);
    
    try {
      const response = await api.put<UpdateResponse>(`/invites/update/${inviteToken}`, {
        statut,
        boissonPreferee: boissonPreferee || null
      });
      const data = response.data;
      if (data && data.success) {
        setSaveMessage("✅ Sauvegardé avec succès !");
        localStorage.removeItem(`invite_${inviteToken}`);
      } else {
        setSaveMessage("❌ Erreur: " + (data?.message || 'Erreur inconnue'));
      }
    } catch (error: any) {
      setSaveMessage("❌ Erreur réseau. Données sauvegardées localement.");
    } finally {
      setIsSaving(false);
      setTimeout(() => setSaveMessage(null), 5000);
    }
  }, [currentMariageId, inviteToken, inviteNom, bride, groom]);

  const handleConfirmPresence = () => {
    const newConfirmedState = !confirmed;
    setConfirmed(newConfirmedState);
    
    if (newConfirmedState) {
      setSaveMessage("✅ Vous avez confirmé votre présence.");
    } else {
      setSaveMessage("❌ Vous avez annulé votre confirmation.");
    }
    
    const statut = newConfirmedState ? 'confirmed' : 'pending';
    saveChoicesToBackend(statut, selectedBoisson || undefined);
  };

  const handleSelectBoisson = (boisson: string) => {
    if (!confirmed) {
      setSaveMessage("⚠️ Veuillez d'abord confirmer votre présence pour choisir une boisson.");
      setTimeout(() => setSaveMessage(null), 3000);
      return;
    }
    
    const newBoisson = selectedBoisson === boisson ? null : boisson;
    setSelectedBoisson(newBoisson);
    
    if (newBoisson) {
      setBoissonNotification(`✅ Vous avez sélectionné: ${newBoisson}`);
      setTimeout(() => setBoissonNotification(null), 3000);
    }
    
    if (confirmed && newBoisson) {
      saveChoicesToBackend('confirmed', newBoisson);
    }
  };

  const handleDownload = () => {
    if (!confirmed) {
      setSaveMessage("⚠️ Veuillez d'abord confirmer votre présence pour télécharger votre QR code.");
      setTimeout(() => setSaveMessage(null), 3000);
      return;
    }
    qrCode.current?.download({
      name: `qr-invitation-${inviteNom.replace(/\s+/g, "-")}`,
      extension: "png",
    });
  };

  useEffect(() => {
    if (inviteToken) {
      const savedData = localStorage.getItem(`invite_${inviteToken}`);
      if (savedData) {
        try {
          const parsedData = JSON.parse(savedData);
          setConfirmed(parsedData.statut === 'confirmed');
          setSelectedBoisson(parsedData.boissonPreferee || null);
          if (parsedData.mariageId && parsedData.mariageId !== 'unknown') {
            setCurrentMariageId(parsedData.mariageId);
          }
        } catch (error) {}
      }
    }
  }, [inviteToken]);

  const generateQRData = useCallback(() => {
    if (inviteToken && inviteToken.trim() !== "") {
      return inviteToken;
    }
    
    let safeTableName = "À déterminer";
    if (tableName && typeof tableName === 'string') {
      const trimmed = tableName.trim();
      if (trimmed !== "" && trimmed !== "null" && trimmed !== "undefined" && trimmed !== "Non assignée") {
        safeTableName = trimmed;
      }
    }
    
    const statutInvite = couple === "oui" ? "Couple" : "Individuel";
    
    const qrContent = `INVITATION MARIAGE\n\nNom: ${inviteNom}\nStatut: ${statutInvite}\nTable: ${safeTableName}\n\n${bride} & ${groom}\n${jourSemaine} ${date} à ${heure}`;
    return qrContent;
  }, [inviteToken, inviteNom, tableName, couple, bride, groom, jourSemaine, date, heure]);

  // Génération initiale du QR code
  useEffect(() => {
    if (!isLoading && !qrInitialized && qrRef.current) {
      const qrData = generateQRData();
      
      qrCode.current = new QRCodeStyling({
        width: 250,
        height: 250,
        data: qrData,
        margin: 10,
        qrOptions: { typeNumber: 0, mode: "Byte", errorCorrectionLevel: "H" },
        dotsOptions: { color: colors.textPrimary, type: "square" },
        backgroundOptions: { color: "#ffffff" },
        cornersSquareOptions: { color: colors.heartColor, type: "square" },
        cornersDotOptions: { color: colors.heartColor, type: "square" },
      });

      if (qrRef.current) {
        qrRef.current.innerHTML = "";
        qrCode.current.append(qrRef.current);
      }
      setQrInitialized(true);
    }
  }, [isLoading, qrInitialized, colors.heartColor, colors.textPrimary, generateQRData]);

  // Force la régénération du QR code quand confirmation change
  useEffect(() => {
    if (confirmed && qrRef.current && !qrInitialized) {
      const qrData = generateQRData();
      
      if (qrCode.current) {
        qrCode.current.update({ data: qrData });
      } else {
        qrCode.current = new QRCodeStyling({
          width: 250,
          height: 250,
          data: qrData,
          margin: 10,
          qrOptions: { typeNumber: 0, mode: "Byte", errorCorrectionLevel: "H" },
          dotsOptions: { color: colors.textPrimary, type: "square" },
          backgroundOptions: { color: "#ffffff" },
          cornersSquareOptions: { color: colors.heartColor, type: "square" },
          cornersDotOptions: { color: colors.heartColor, type: "square" },
        });
        
        if (qrRef.current) {
          qrRef.current.innerHTML = "";
          qrCode.current.append(qrRef.current);
        }
      }
      setQrInitialized(true);
    }
  }, [confirmed, qrInitialized, colors.heartColor, colors.textPrimary, generateQRData]);

  const [interactions, setInteractions] = useState({ aimant: 25, love: 40, content: 20, livre: 15 });
  const [percentages, setPercentages] = useState(interactions);

  const handleClickInteraction = (type: "aimant" | "love" | "content" | "livre") => {
    setInteractions((prev) => {
      const updated = { ...prev, [type]: prev[type] + 1 };
      const total = updated.aimant + updated.love + updated.content + updated.livre;
      setPercentages({
        aimant: Math.round((updated.aimant / total) * 100),
        love: Math.round((updated.love / total) * 100),
        content: Math.round((updated.content / total) * 100),
        livre: Math.round((updated.livre / total) * 100),
      });
      return updated;
    });
  };

  useEffect(() => {
    const eventDate = new Date(`${dateISO}T${heureISO}`);
    if (isNaN(eventDate.getTime())) {
      setCountdown("Date invalide");
      return;
    }
    
    const updateCountdown = () => {
      const now = new Date();
      const diff = eventDate.getTime() - now.getTime();
      if (diff <= 0) {
        setCountdown("C'est aujourd'hui !");
        return;
      }
      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
      const minutes = Math.floor((diff / (1000 * 60)) % 60);
      const seconds = Math.floor((diff / 1000) % 60);
      setCountdown(`${days}j ${hours}h ${minutes}m ${seconds}s`);
    };
    
    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    
    const deadline = new Date(eventDate);
    deadline.setDate(deadline.getDate() - 12);
    setRsvpDeadline(deadline.toLocaleDateString("fr-FR", {
      day: "numeric", month: "long", year: "numeric"
    }));
    
    return () => clearInterval(interval);
  }, [dateISO, heureISO]);

  if (isLoading) {
    return <Preloader 
      bride={bride}
      groom={groom}
      colors={{
        background: colors.background,
        heart: colors.heartColor,
        text: colors.textPrimary,
        accent: colors.buttonPrimary
      }}
      durationMs={980}
      onFinish={() => setIsLoading(false)}
    />;
  }

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@300;400;500;600;700&family=Montserrat:wght@200;300;400;500&display=swap');

        .save-message {
          position: fixed;
          top: 20px;
          right: 20px;
          padding: 16px 24px;
          border-radius: 12px;
          box-shadow: 0 10px 30px rgba(0,0,0,0.1);
          z-index: 2000;
          max-width: 400px;
          font-weight: 400;
          animation: slideIn 0.3s ease-out;
          backdrop-filter: blur(10px);
          font-family: 'Montserrat', sans-serif;
          font-size: 14px;
          border-left: 4px solid;
        }
        
        .boisson-notification {
          position: fixed;
          top: 80px;
          right: 20px;
          padding: 16px 24px;
          border-radius: 12px;
          box-shadow: 0 10px 30px rgba(0,0,0,0.1);
          z-index: 2000;
          max-width: 400px;
          font-weight: 400;
          animation: slideIn 0.3s ease-out;
          backdrop-filter: blur(10px);
          background: linear-gradient(135deg, #1a3a1a, #2a5a2a);
          color: #d4edda;
          border-left: 4px solid #28a745;
          font-family: 'Montserrat', sans-serif;
        }
        
        @keyframes slideIn {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }

        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        @keyframes float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }

        @keyframes rotateSlow {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>

      {saveMessage && (
        <div className="save-message" style={{
          backgroundColor: saveMessage.includes('✅') ? 'rgba(212, 237, 218, 0.95)' : 
                          saveMessage.includes('⚠️') ? 'rgba(255, 243, 205, 0.95)' : 'rgba(248, 215, 218, 0.95)',
          color: saveMessage.includes('✅') ? '#155724' : 
                 saveMessage.includes('⚠️') ? '#856404' : '#721c24',
          borderLeftColor: saveMessage.includes('✅') ? '#28a745' : 
                          saveMessage.includes('⚠️') ? '#ffc107' : '#dc3545'
        }}>
          {saveMessage}
        </div>
      )}

      {boissonNotification && (
        <div className="boisson-notification">
          {boissonNotification}
        </div>
      )}

      <div 
        className="classique-hero" 
        ref={heroRef}
        style={{ backgroundColor: colors.background }}
      >
        <div className="classique-floating-elements">
          <div className="floating-circle" style={{ backgroundColor: colors.heartColor }}></div>
          <div className="floating-circle-2" style={{ backgroundColor: colors.heartColor }}></div>
          <div className="floating-line" style={{ backgroundColor: colors.accentLine }}></div>
          <div className="floating-line-2" style={{ backgroundColor: colors.accentLine }}></div>
        </div>

        <section className="hero-classique">
          <div 
            className="hero-classique-background"
            style={{ 
              backgroundImage: `url(${getUploadedImagePath(photoBackground)})`,
              transform: `translateY(${scrollY * 0.15}px)`,
            }}
          ></div>
          
          <div className="hero-classique-overlay" style={{ backgroundColor: colors.photoOverlay }}></div>
          
          <div className="hero-classique-pattern" style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M30 5 L55 30 L30 55 L5 30 Z' fill='none' stroke='${encodeURIComponent(colors.accentLine)}' stroke-width='0.5' opacity='0.2'/%3E%3C/svg%3E")` }}></div>
          
          <div className="hero-classique-content">
            
            <h1 className="hero-classique-names">
              <span className="hero-classique-bride" style={{ color: colors.textPrimary }}>{bride}</span>
              <span className="hero-classique-ampersand" style={{ color: colors.heartColor }}>✧</span>
              <span className="hero-classique-groom" style={{ color: colors.textPrimary }}>{groom}</span>
            </h1>
            
            <div className="hero-classique-ribbon">
              <div 
                className="ribbon-left" 
                style={{ borderColor: `transparent transparent transparent ${colors.accentLine}` }}
              ></div>
              <div 
                className="ribbon-center" 
                style={{ backgroundColor: colors.accentLine, color: '#ffffff' }}
              >
                {jourSemaine} {date}
              </div>
              <div 
                className="ribbon-right" 
                style={{ borderColor: `transparent ${colors.accentLine} transparent transparent` }}
              ></div>
            </div>
            
            <p className="hero-classique-time" style={{ color: colors.textPrimary }}>à {heure}</p>
            
            <div className="hero-classique-floral left">
              <svg width="80" height="150" viewBox="0 0 80 150" fill="none">
                <path d="M40 0 Q20 30 30 60 Q40 90 20 120 Q10 140 30 150" stroke={colors.accentLine} strokeWidth="1" fill="none" opacity="0.6" strokeDasharray="3 3"/>
                <circle cx="35" cy="30" r="3" fill={colors.heartColor} opacity="0.4"/>
                <circle cx="25" cy="70" r="4" fill={colors.heartColor} opacity="0.4"/>
                <circle cx="35" cy="110" r="3" fill={colors.heartColor} opacity="0.4"/>
              </svg>
            </div>
            
            <div className="hero-classique-floral right">
              <svg width="80" height="150" viewBox="0 0 80 150" fill="none">
                <path d="M40 0 Q60 30 50 60 Q40 90 60 120 Q70 140 50 150" stroke={colors.accentLine} strokeWidth="1" fill="none" opacity="0.6" strokeDasharray="3 3"/>
                <circle cx="45" cy="30" r="3" fill={colors.heartColor} opacity="0.4"/>
                <circle cx="55" cy="70" r="4" fill={colors.heartColor} opacity="0.4"/>
                <circle cx="45" cy="110" r="3" fill={colors.heartColor} opacity="0.4"/>
              </svg>
            </div>
            
            <div className="hero-classique-scroll">
              <span className="hero-classique-scroll-text" style={{ color: colors.textPrimary }}>Défiler</span>
              <div className="hero-classique-scroll-line" style={{ backgroundColor: colors.accentLine }}></div>
            </div>
          </div>
        </section>

        <section className="invitation-classique-section">
          <div className="invitation-classique-container">
            <div className="invitation-classique-frame">
              <div className="frame-corner top-left" style={{ borderColor: colors.accentLine }}></div>
              <div className="frame-corner top-right" style={{ borderColor: colors.accentLine }}></div>
              <div className="frame-corner bottom-left" style={{ borderColor: colors.accentLine }}></div>
              <div className="frame-corner bottom-right" style={{ borderColor: colors.accentLine }}></div>
              
              <div className="invitation-classique-card" style={{ backgroundColor: colors.inviteBoxBackground }}>
                <div className="invitation-classique-header">
                  <span className="invitation-classique-label" style={{ color: colors.heartColor }}>INVITATION</span>
                  <h2 className="invitation-classique-name" style={{ color: colors.textPrimary }}>{inviteNom}</h2>
                  <div className="invitation-classique-divider" style={{ backgroundColor: colors.accentLine }}></div>
                </div>
                
                <p className="invitation-classique-message" style={{ color: colors.textPrimary }}>
                  Nous avons le plaisir de vous convier à célébrer notre union.
                  <br />Votre présence nous touche profondément.
                </p>
                
                <div className="invitation-classique-countdown">
                  <span className="countdown-classique-label" style={{ color: colors.textSecondary }}>J-</span>
                  <span className="countdown-classique-value" style={{ color: colors.heartColor }}>{countdown}</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {invitationImage && (
          <section className="invitation-image-classique-section">
            <div className="invitation-image-classique-container">
              <div className="image-classique-frame" style={{ borderColor: colors.accentLine }}>
                <img 
                  src={getUploadedImagePath(invitationImage)} 
                  alt="Invitation officielle" 
                  className="invitation-classique-image"
                />
                <div className="image-classique-overlay" style={{ background: `linear-gradient(45deg, ${colors.heartColor}20, transparent)` }}></div>
              </div>
            </div>
          </section>
        )}

        <section className="rsvp-classique-section">
          <h2 className="classique-section-title" style={{ color: colors.textPrimary }}>Confirmation</h2>
          <p className="classique-section-subtitle" style={{ color: colors.textSecondary }}>Avant le {rsvpDeadline}</p>
          
          <div className="rsvp-classique-card" style={{ backgroundColor: colors.boxBackground, borderColor: colors.accentLine }}>
            <div className="rsvp-classique-waxseal" style={{ backgroundColor: colors.heartColor }}>
              <span>✧</span>
            </div>
            
            <button 
              className={`rsvp-classique-button ${confirmed ? 'cancel' : ''}`}
              style={{ 
                backgroundColor: confirmed ? 'transparent' : colors.buttonPrimary,
                color: confirmed ? colors.heartColor : '#ffffff',
                borderColor: colors.accentLine
              }}
              onClick={handleConfirmPresence}
              disabled={isSaving}
            >
              {isSaving ? '...' : (confirmed ? '✓ Annuler' : 'Confirmer ma présence')}
            </button>
            
            {confirmed && (
              <div className="rsvp-classique-confirmation">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" stroke={colors.accentLine} strokeWidth="2" fill="none"/>
                  <path d="M8 12l3 3 6-6" stroke={colors.accentLine} strokeWidth="2" strokeLinecap="round"/>
                </svg>
                <span style={{ color: colors.textPrimary }}>Présence confirmée</span>
              </div>
            )}
          </div>
        </section>

        <section className="qr-classique-section">
          <h2 className="classique-section-title" style={{ color: colors.textPrimary }}>QR Code d'accès</h2>
          <div className="qr-classique-card" style={{ backgroundColor: colors.boxBackground, borderColor: colors.accentLine }}>
            {!confirmed ? (
              <div style={{ textAlign: 'center', padding: '30px' }}>
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginBottom: '15px' }}>
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" fill={colors.heartColor}/>
                </svg>
                <p style={{ color: colors.textPrimary, marginBottom: '15px' }}>
                  Veuillez d'abord confirmer votre présence pour accéder à votre QR code.
                </p>
                <button 
                  onClick={() => document.querySelector('.rsvp-classique-button')?.scrollIntoView({ behavior: 'smooth' })}
                  style={{
                    background: colors.heartColor,
                    color: '#fff',
                    border: 'none',
                    padding: '10px 25px',
                    borderRadius: '30px',
                    cursor: 'pointer'
                  }}
                >
                  Confirmer ma présence
                </button>
              </div>
            ) : (
              <div style={{ width: '100%', textAlign: 'center' }}>
                <div 
                  className="qr-classique-wrapper" 
                  ref={qrRef}
                  style={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    alignItems: 'center',
                    marginBottom: '20px'
                  }}
                ></div>
                <button 
                  className="qr-classique-button"
                  style={{ 
                    backgroundColor: colors.buttonPrimary, 
                    borderColor: colors.accentLine,
                    color: '#ffffff'
                  }}
                  onClick={handleDownload}
                >
                  Télécharger
                </button>
              </div>
            )}
          </div>
        </section>

        {(cleanedBoissonsSansAlcool.length > 0 || cleanedBoissonsAvecAlcool.length > 0) && (
          <section className="boissons-classique-section">
            <h2 className="classique-section-title" style={{ color: colors.textPrimary }}>Boissons</h2>
            
            <div className="boissons-classique-card" style={{ backgroundColor: colors.boxBackground, borderColor: colors.accentLine }}>
              <div className="boissons-classique-decoration">
                <span className="glass-icon" style={{ color: colors.heartColor }}>🥂</span>
              </div>
              
              {!confirmed ? (
                <div style={{ textAlign: 'center', padding: '30px' }}>
                  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginBottom: '15px' }}>
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" fill={colors.heartColor}/>
                  </svg>
                  <p style={{ color: colors.textPrimary, marginBottom: '15px' }}>
                    Veuillez d'abord confirmer votre présence pour choisir votre boisson.
                  </p>
                  <button 
                    onClick={() => document.querySelector('.rsvp-classique-button')?.scrollIntoView({ behavior: 'smooth' })}
                    style={{
                      background: colors.heartColor,
                      color: '#fff',
                      border: 'none',
                      padding: '10px 25px',
                      borderRadius: '30px',
                      cursor: 'pointer'
                    }}
                  >
                    Confirmer ma présence
                  </button>
                </div>
              ) : (
                <>
                  {cleanedBoissonsSansAlcool.length > 0 && (
                    <div className="boissons-classique-category">
                      <h3 style={{ color: colors.heartColor }}>Sans alcool</h3>
                      <div className="boissons-classique-list">
                        {cleanedBoissonsSansAlcool.map((item, index) => (
                          <button
                            key={index}
                            className={`boisson-classique-item ${selectedBoisson === item ? 'selected' : ''}`}
                            style={{ 
                              backgroundColor: selectedBoisson === item ? colors.buttonPrimary : 'transparent',
                              color: selectedBoisson === item ? '#ffffff' : colors.textPrimary,
                              borderColor: colors.accentLine
                            }}
                            onClick={() => handleSelectBoisson(item)}
                          >
                            {item}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {cleanedBoissonsAvecAlcool.length > 0 && (
                    <div className="boissons-classique-category">
                      <h3 style={{ color: colors.heartColor }}>Avec alcool</h3>
                      <div className="boissons-classique-list">
                        {cleanedBoissonsAvecAlcool.map((item, index) => (
                          <button
                            key={index}
                            className={`boisson-classique-item ${selectedBoisson === item ? 'selected' : ''}`}
                            style={{ 
                              backgroundColor: selectedBoisson === item ? colors.buttonPrimary : 'transparent',
                              color: selectedBoisson === item ? '#ffffff' : colors.textPrimary,
                              borderColor: colors.accentLine
                            }}
                            onClick={() => handleSelectBoisson(item)}
                          >
                            {item}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {selectedBoisson && (
                    <p className="boisson-classique-selected" style={{ color: colors.heartColor }}>
                      ✓ {selectedBoisson}
                    </p>
                  )}
                </>
              )}
            </div>
          </section>
        )}

        {livreOrActive && (
          <section className="livre-classique-section">
            <h2 className="classique-section-title" style={{ color: colors.textPrimary }}>Livre d'or</h2>
            <div className="livre-classique-card" style={{ backgroundColor: colors.boxBackground, borderColor: colors.accentLine }}>
              <div className="livre-classique-quill" style={{ color: colors.heartColor }}>✒️</div>
              <p className="livre-classique-text" style={{ color: colors.textPrimary }}>
                Laissez votre message
              </p>
              <form 
                className="livre-classique-form"
                onSubmit={(e) => {
                  e.preventDefault();
                  const message = (e.currentTarget.elements.namedItem('message') as HTMLTextAreaElement).value;
                  
                  const messageComplet = `LIVRE D'OR

De la part de : ${inviteNom}
Message : "${message}"

Merci infiniment pour votre message. Nous vous sommes très reconnaissants.`;
                  
                  const url = `https://wa.me/${numeroMarie}?text=${encodeURIComponent(messageComplet)}`;
                  window.open(url, "_blank");
                  
                  alert("✅ Message envoyé avec succès !");
                  (e.target as HTMLFormElement).reset();
                }}
              >
                <textarea 
                  name="message" 
                  placeholder="Votre message..."
                  className="livre-classique-textarea"
                  style={{ borderColor: colors.accentLine, color: colors.textPrimary, backgroundColor: 'rgba(255,255,255,0.5)' }}
                  required
                />
                <button 
                  type="submit" 
                  className="livre-classique-submit"
                  style={{ backgroundColor: colors.buttonPrimary, borderColor: colors.accentLine, color: '#ffffff' }}
                >
                  Envoyer
                </button>
              </form>
            </div>
          </section>
        )}

        <section className="interactions-classique-section">
          <h2 className="classique-section-title" style={{ color: colors.textPrimary }}>Votre avis</h2>
          <div className="interactions-classique-card" style={{ backgroundColor: colors.boxBackground, borderColor: colors.accentLine }}>
            <p className="interactions-classique-text" style={{ color: colors.textPrimary }}>
              Comment trouvez-vous cette invitation ?
            </p>
            
            <div className="interactions-classique-buttons">
              {[
                { type: 'aimant', emoji: '👌' },
                { type: 'love', emoji: '❤️' },
                { type: 'content', emoji: '😁' },
                { type: 'livre', emoji: '🤭' }
              ].map((btn) => (
                <button 
                  key={btn.type}
                  className="interaction-classique-btn" 
                  style={{ backgroundColor: colors.heartColor }}
                  onClick={() => handleClickInteraction(btn.type as any)}
                >
                  {btn.emoji}
                </button>
              ))}
            </div>
            
            <div className="interactions-classique-stats">
              <div className="stat-classique">
                <span className="stat-classique-label" style={{ color: colors.textPrimary }}>👌 Super</span>
                <div className="progress-classique">
                  <div className="progress-classique-fill" style={{ width: `${percentages.aimant}%`, backgroundColor: colors.accentLine }}></div>
                </div>
                <span className="stat-classique-value" style={{ color: colors.heartColor }}>{percentages.aimant}%</span>
              </div>
              <div className="stat-classique">
                <span className="stat-classique-label" style={{ color: colors.textPrimary }}>❤️ Love</span>
                <div className="progress-classique">
                  <div className="progress-classique-fill" style={{ width: `${percentages.love}%`, backgroundColor: colors.accentLine }}></div>
                </div>
                <span className="stat-classique-value" style={{ color: colors.heartColor }}>{percentages.love}%</span>
              </div>
              <div className="stat-classique">
                <span className="stat-classique-label" style={{ color: colors.textPrimary }}>😁 Content</span>
                <div className="progress-classique">
                  <div className="progress-classique-fill" style={{ width: `${percentages.content}%`, backgroundColor: colors.accentLine }}></div>
                </div>
                <span className="stat-classique-value" style={{ color: colors.heartColor }}>{percentages.content}%</span>
              </div>
              <div className="stat-classique">
                <span className="stat-classique-label" style={{ color: colors.textPrimary }}>🤭 Mignon</span>
                <div className="progress-classique">
                  <div className="progress-classique-fill" style={{ width: `${percentages.livre}%`, backgroundColor: colors.accentLine }}></div>
                </div>
                <span className="stat-classique-value" style={{ color: colors.heartColor }}>{percentages.livre}%</span>
              </div>
            </div>
          </div>
        </section>

        <section ref={backRef} className="back-classique-section">
          <div 
            className="back-classique-container"
            style={{ 
              backgroundImage: `url(${getUploadedImagePath(photoBackground)})`,
              backgroundAttachment: 'fixed',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }}
          ></div>
          <div className="back-classique-overlay" style={{ backgroundColor: colors.photoOverlay }}></div>
        </section>

        <section className="map-classique-section">
          <h2 className="classique-section-title" style={{ color: colors.textPrimary }}>Comment venir ?</h2>
          <div className="map-classique-card" style={{ backgroundColor: colors.boxBackground, borderColor: colors.accentLine }}>
            <a href={googleMapsViewUrl} target="_blank" rel="noopener noreferrer">
              <img src="/assets/elegant/carte.png" alt="Carte du lieu" className="map-classique-image" />
            </a>
            <a 
              href={googleMapsViewUrl} 
              target="_blank" 
              rel="noopener noreferrer" 
              className="map-classique-button"
              style={{ backgroundColor: colors.buttonPrimary, borderColor: colors.accentLine, color: '#ffffff' }}
            >
              Itinéraire
            </a>
          </div>
        </section>

        <section className="contact-classique-section">
          <h2 className="classique-section-title" style={{ color: colors.textPrimary }}>Contact</h2>
          <div className="contact-classique-card" style={{ backgroundColor: colors.boxBackground, borderColor: colors.accentLine }}>
            <p className="contact-classique-text" style={{ color: colors.textPrimary }}>
              Une question ? Écrivez-nous
            </p>
            <a 
              href={`https://wa.me/${numeroPicApic}?text=${encodeURIComponent("Bonjour, j'ai une question concernant le mariage.")}`}
              target="_blank"
              rel="noopener noreferrer"
              className="contact-classique-button"
              style={{ backgroundColor: colors.buttonPrimary, borderColor: colors.accentLine, color: '#ffffff' }}
            >
              <span>✉️</span> WhatsApp
            </a>
          </div>
        </section>

        <footer className="classique-footer" style={{ backgroundColor: colors.footerBackground }}>
          <div className="classique-footer-content">
            <p className="classique-copyright" style={{ color: '#ffffff' }}>© {new Date(dateISO).getFullYear()} • {bride} & {groom}</p>
            <div className="classique-social-links">
              <a href={socialLinks.instagram} target="_blank" rel="noopener noreferrer" className="classique-social-link">
                {getSocialIcon('instagram', '#ffffff', 20)}
              </a>
              <a href={socialLinks.tiktok} target="_blank" rel="noopener noreferrer" className="classique-social-link">
                {getSocialIcon('tiktok', '#ffffff', 20)}
              </a>
              <a href={socialLinks.facebook} target="_blank" rel="noopener noreferrer" className="classique-social-link">
                {getSocialIcon('facebook', '#ffffff', 20)}
              </a>
            </div>
            <p className="classique-credits" style={{ color: '#ffffff', opacity: 0.8 }}>
              <a href={socialLinks.picapic} target="_blank" rel="noopener noreferrer" style={{ color: '#ffffff', textDecoration: 'none' }}>
                By PicApic
              </a>
            </p>
          </div>
        </footer>
      </div>
    </>
  );
};

export default ClassiqueHero;